import { useCallback, useEffect, useState } from 'react';

import API from 'my-core/api';
import { CLASS_REP_GB_DISCOUNT_PERCENT } from 'my-core/class-rep-course-utils';
import { REFERRAL_DISCOUNT_AMOUNT } from 'my-core/constants';
import { calculateDiscount, getDiscountCodeCheckoutLabel } from 'my-core/discount-code-utils';
import { getSubscriptionProductDiscountPercent, useCurrentUserSubscription } from 'my-core/subscription-utils';

import CheckoutLineItems from 'my-components/Checkout/CheckoutLineItems';
import DiscountCodeInput from 'my-components/DiscountCodeInput';
import PaymentForm from 'my-components/PaymentForm';

import Button from '@mui/material/Button';

export default function StudySessionBundleCheckoutPayment({
  currentUser,
  loading,
  onClose,
  onPaymentSuccess,
  pricingInfo,
  studySessionBundle,
  studySessionBundleUser,
}) {
  const { countryCode, currency, salesTax } = pricingInfo;

  const listPrice = studySessionBundle.price || 0;
  let price = listPrice;

  const subscription = useCurrentUserSubscription();
  const classRepDiscountPercent = currentUser.has_class_rep_discount && CLASS_REP_GB_DISCOUNT_PERCENT;
  const subscriptionDiscountPercent = !classRepDiscountPercent && getSubscriptionProductDiscountPercent(subscription);
  const [discountCodeVal, setDiscountCodeVal] = useState(null);
  const discountCode = !subscriptionDiscountPercent && !classRepDiscountPercent && discountCodeVal;

  let subDiscount, classRepDiscount, discountCodeDiscount;
  if (classRepDiscountPercent) {
    classRepDiscount = (price * classRepDiscountPercent) / 100;
    price -= classRepDiscount;
  } else if (subscriptionDiscountPercent) {
    subDiscount = (price * subscriptionDiscountPercent) / 100;
    price -= subDiscount;
  } else {
    discountCodeDiscount = calculateDiscount(discountCode, price);
    price -= discountCodeDiscount;
  }

  const referralDiscount = currentUser.is_referred ? Math.min(REFERRAL_DISCOUNT_AMOUNT, price) : 0;
  price -= referralDiscount;

  const creditedAmount = Math.min(price, currentUser.course_credits || 0);
  price -= creditedAmount;

  const isDiscounted = !!(
    subDiscount ||
    classRepDiscount ||
    discountCodeDiscount ||
    referralDiscount ||
    creditedAmount
  );
  const taxAmount = Math.round(price * salesTax) / 100;
  const total = Math.round((price + taxAmount) * 100) / 100;
  const paymentDetails = {
    lineItems: [
      { label: studySessionBundle.title, amount: listPrice, highlight: !isDiscounted },
      subDiscount && {
        label: 'Wizeprep Plus Member',
        caption: `Additional ${subscriptionDiscountPercent}% Discount`,
        amount: -subDiscount,
      },
      classRepDiscount && { label: `Class Rep ${classRepDiscountPercent}% Discount`, amount: -classRepDiscount },
      discountCodeDiscount && {
        label: getDiscountCodeCheckoutLabel(discountCode),
        amount: -discountCodeDiscount,
      },
      referralDiscount && { label: 'Refer a Friend Discount', amount: -referralDiscount },
      creditedAmount && { label: 'Credits', amount: -creditedAmount },
      isDiscounted && { label: 'Discounted Subtotal', amount: price, highlight: true },
      taxAmount && { label: `Sales Tax (${salesTax}%)`, amount: taxAmount },
      { label: 'Total', amount: total },
    ].filter(Boolean),
    total,
    tax: taxAmount,
    currency,
  };

  const [purchaseIntentRequest, setPurchaseIntentRequest] = useState();
  const purchaseIntent = purchaseIntentRequest?.intent;
  const piRequestKey = `ssb_${studySessionBundle.id}_dc_${discountCode?.id}`;
  useEffect(() => {
    if (studySessionBundle.id && !!total && !studySessionBundleUser) {
      setPurchaseIntentRequest();
      API.postJSON(`/api/v1/study_session_bundles/${studySessionBundle.id}/create_purchase_intent`, {
        discount_code_id: discountCode?.id,
      })
        .then(intent => {
          setPurchaseIntentRequest({ intent, key: piRequestKey });
        })
        .catch(error => {
          setPurchaseIntentRequest({ error, key: piRequestKey });
        });
    }
  }, [studySessionBundle.id, discountCode?.id, total, studySessionBundleUser, piRequestKey]);

  const priceMatch = Math.round(total * 100) === purchaseIntent?.amount;

  const handlePaymentSuccess = useCallback(
    ({ paymentIntent }) => onPaymentSuccess({ paymentIntent, total, discountCode, currency }),
    [onPaymentSuccess, total, discountCode, currency],
  );

  return (
    <>
      {!subscriptionDiscountPercent && !classRepDiscountPercent && (
        <DiscountCodeInput
          ButtonProps={{ variant: 'outlined' }}
          defaultQuery={discountCode?.code}
          onVerified={setDiscountCodeVal}
          productId={studySessionBundle.id}
          productType="StudySessionBundle"
          TextFieldProps={{ placeholder: 'Discount code' }}
        />
      )}
      <CheckoutLineItems css={({ spacing }) => ({ margin: spacing(2, 0) })} paymentDetails={paymentDetails} />
      {total ?
        <PaymentForm
          amount={purchaseIntent?.amount || false}
          countryCode={countryCode}
          currency={currency}
          disabled={loading || !priceMatch}
          error={
            purchaseIntentRequest?.error ||
            (purchaseIntentRequest?.key === piRequestKey &&
              !priceMatch &&
              'There was an error preparing for your payment. Please refresh the page. If this issue persists, please contact support.')
          }
          onCancel={onClose}
          onSuccess={handlePaymentSuccess}
          paymentIntent={purchaseIntent}
          paymentMethod={purchaseIntent?.payment_method}
          submitting={loading}
        />
      : <div css={{ textAlign: 'right' }}>
          <Button
            disabled={loading}
            onClick={() => onPaymentSuccess({ total, discountCode, currency })}
            variant="contained"
          >
            Complete Purchase
          </Button>
        </div>
      }
    </>
  );
}
